/* eslint-disable max-len */
import { h, Fragment } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from 'utils/image';
import { IconGift, IconCarat, IconCheckbox } from 'utils/icon';
import formatProductTitle from 'utils/formatProductTitle';
import { MONOGRAM_PROPERTIES } from 'utils/constants';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { withCartContext } from './cart-context';
import WishlistContext, {
  useWishlist,
} from '../utils/gift-reggie/wishlist-context';

const CartPageItem = (props) => {
  const { cart, line, configuredGiftProduct, isMiniCart } = props;
  const { addToWishlist } = useWishlist(WishlistContext);
  const [compareAtPrice, setCompareAtPrice] = useState(0);
  const [hideGift, setHideGift] = useState(true);
  const technicalDataEl = document.querySelector('[data-technical]');
  const lineTitle = formatProductTitle(
    line.product_title,
    line.options_with_values,
  );
  const isLoggedIn =
    technicalDataEl?.innerHTML &&
    JSON.parse(technicalDataEl.innerHTML)?.customer;

  const linesGroupedByGroupID = cart?.state?.items?.reduce((acc, item) => {
    const currentGroupID = item.properties?._groupID;
    if (!currentGroupID) return acc;
    if (!acc[currentGroupID]) acc[currentGroupID] = [];
    acc[currentGroupID].push(item);
    return acc;
  }, {});

  const lineGroupID = line.properties?._groupID;
  const lineGroup = linesGroupedByGroupID[lineGroupID];
  const giftProduct = lineGroup?.find(
    (item) => item.properties?._charge_type === 'gift',
  );

  useEffect(() => {
    const fetchData = async () => {
      const storefront = Storefront.getInstance();
      const response = await storefront.request({
        query: queries.cartProductByHandle(),
        variables: { handle: line.handle },
      });
      const compareAtPriceResponse =
        response?.data?.productByHandle?.compareAtPriceRange?.minVariantPrice
          ?.amount;
      setCompareAtPrice(
        compareAtPriceResponse ? Number(compareAtPriceResponse) : 0,
      );

      const hideGiftResponse = response?.data?.productByHandle?.hideGift?.value;
      setHideGift(hideGiftResponse === 'true');
    };

    fetchData();
  }, [line.handle]);

  const handleAddToWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoggedIn) {
      handleRemove([line.variant_id]);
      addToWishlist(line.variant_id);
    } else {
      window.location.pathname = '/account/login';
    }
  };

  const adjustQuantity = (quantity) => {
    const groupID = line.properties?._groupID;

    if (groupID) {
      const group = linesGroupedByGroupID[groupID];
      const payload = group.map((item) => ({
        id: item.key,
        quantity,
        attributes: item.properties,
      }));
      cart.update(payload);
      return;
    }

    cart.update([
      {
        id: line.key,
        quantity,
        attributes: line.properties,
      },
    ]);
  };

  const handleRemove = useCallback(async () => {
    await cart.remove([line.id]);
  }, [line, cart]);

  const {
    discountCodeMessaging,
    finalSaleDetailsMessage,
    finalSaleBannerMessage,
    monogramFinalSaleMessage,
    monogramShippingWarning,
  } = JSON.parse(document.querySelector('[data-global-settings]').textContent);

  const isFinalSale = Boolean(
    line.properties && line.properties['_tag_:final-sale'],
  );

  const DropShip = Boolean(
    line.properties && line.properties['_tag_:drop-ship'],
  );

  const hasCompareAtPrice = !Number.isNaN(compareAtPrice) && compareAtPrice > 0;

  const originalPrice = line.original_line_price / 100;
  const hasDiscount = line?.line_level_total_discount > 0;

  // Extract discount allocations from the line item
  const discountAllocations = line?.line_level_discount_allocations || [];

  // Check if there are any discounts applied
  const hasDiscountAllocations = discountAllocations?.length > 0;

  const showRedFinalPrice = Boolean(hasCompareAtPrice || hasDiscount);

  const isMonogrammed = Boolean(
    line.properties &&
      Object.keys(line.properties).some((key) =>
        MONOGRAM_PROPERTIES.includes(key),
      ),
  );
  const canApplyDiscountCode = Boolean(
    line.properties && line.properties['_tag_:discount-code'],
  );

  const monogramPrice = () => {
    if (!line.properties?._groupID) return 0;

    const group = linesGroupedByGroupID[line.properties._groupID];
    const monogramLine = group?.find(
      (item) => item.properties?._charge_type === 'monogram',
    );

    if (monogramLine) {
      const amount = formatMoney({
        amount: monogramLine.price / 100,
      });

      if (monogramLine.final_line_price === 0) {
        return `
          Monogram Added <span class="text-text-disabled">${window.TMW.Strings.cart.giftWrapProductHeadingVip} <s>${amount})</s></span>
        `;
      }

      return `Monogram Added ${amount}`;
    }

    return formatMoney({
      amount: 0,
    });
  };

  const removeMonogram = useCallback(async () => {
    const group = linesGroupedByGroupID[line.properties._groupID];
    const monogramLine = group?.find(
      (item) => item.properties?._charge_type === 'monogram',
    );

    if (monogramLine) cart.remove([monogramLine.key]);

    // Remove monogram properties from line
    const currentProperties = line.properties;
    const newProperties = [];
    Object.keys(currentProperties).forEach((key) => {
      if (key === '_charge_type' && currentProperties[key] === 'gift') {
        newProperties.push({
          key: '_charge_type',
          value: 'gift',
        });
      }

      if (
        ![...MONOGRAM_PROPERTIES, '_groupID', '_tag_:final-sale'].includes(key)
      ) {
        newProperties.push({
          key,
          value: currentProperties[key],
        });
      }
    });

    // Shopify doesn't remove all properties if the array is empty
    if (newProperties.length === 0) {
      newProperties.push({
        key: '_clear_properties',
        value: 'true',
      });
    }

    const payload = {
      id: line.key,
      attributes: newProperties,
    };
    await cart.updateAttributes(payload);
  }, [line, cart]);

  const openGift = (e) => {
    e.preventDefault();
    const openGiftEvent = new CustomEvent('openGift', {
      detail: {
        line,
        hideGift,
      },
    });
    window.dispatchEvent(openGiftEvent);
  };

  const monogramProperties = () => {
    if (!line.properties) return null;

    const separator = isMiniCart ? ', ' : ' | ';
    const separatorClass = isMiniCart ? 'leading-none' : 'px-2 leading-none';

    const monogramLineItemProperties = Object.entries(line.properties)
      .filter((prop) => MONOGRAM_PROPERTIES.includes(prop[0]))
      .map((prop) => prop[1]);
    return (
      <div className="body-2 mt-5">
        {monogramLineItemProperties.map((part, index) => (
          <span key={index}>
            <span className="body-2">{part}</span>
            <span className={separatorClass}>
              {index !== monogramLineItemProperties.length - 1 && separator}
            </span>
          </span>
        ))}
      </div>
    );
  };

  const userStatus =
    isLoggedIn && isLoggedIn.tags
      ? isLoggedIn.tags.includes('swell_vip_platinum')
      : false;

  const isDonation = Boolean(line?.vendor === 'DailyKarma');

  const els = {
    default: {
      finalSaleBanner: isFinalSale && (
        <p className="final-sale-messaging uppercase body-3 text-sale">
          {finalSaleBannerMessage}
        </p>
      ),
      finalSaleDetails: isFinalSale && !isMonogrammed && (
        <p className="final-sale-messaging mt-5 body-3 text-sale md:text-sm md:mt-6">
          {finalSaleDetailsMessage}
        </p>
      ),
      variantInfo: line.options_with_values.map((item, index) => (
        <p key={index} className={`body-3 text-text-default md:text-sm`}>
          {item.name}: {item.value}
        </p>
      )),
      monogramInfo: (
        <div className="monogrammed">
          {monogramProperties()}
          <h4
            className="font-GTAmerica font-medium mt-5 text-xs text-text-default md:text-sm"
            dangerouslySetInnerHTML={{ __html: monogramPrice() }}
          ></h4>
          <p className="body-3 text-sale md:text-sm">
            {monogramFinalSaleMessage}
          </p>
          <p className="body-3 text-text-default md:text-sm">
            {monogramShippingWarning}
          </p>
          <button
            className="text-xs font-GTAmerica underline underline-offset-[2.5px] mb-2.5 md:text-sm md:mb-3.5"
            onClick={removeMonogram}
          >
            {window.TMW.Strings.cart.removeMonogram}
          </button>
        </div>
      ),
    },
    minicart: {
      finalSaleBanner: isFinalSale && (
        <p className="final-sale-messaging body-4 text-sale mt-2">
          {isMonogrammed ? finalSaleBannerMessage : finalSaleDetailsMessage}
        </p>
      ),
      variantInfo: (
        <p className={`body-3 text-text-default`}>
          {line.options_with_values.reduce((acc, { value }) => {
            let result = acc;
            result += result && ' / ';
            result += value;
            return result;
          }, '')}
        </p>
      ),
      activeCoupons: (
        <div className={isMiniCart ? 'mt-2' : 'mt-2.5 md:mt-5'}>
          {discountAllocations.map((discountAllocation, index) => (
            <p className="body-4 font-medium" key={index}>
              {discountAllocation.discount_application.title} applied
            </p>
          ))}
        </div>
      ),
      monogramInfo: (
        <div className="max-w-[195px]">
          <h4 className="font-GTAmerica mt-5 text-text-default mb-[4px] flex justify-between body-3 ">
            <span dangerouslySetInnerHTML={{ __html: monogramPrice() }}></span>
          </h4>
          <div className="border-2 border-gray-200">
            <input
              className="hidden peer"
              type="checkbox"
              id={`monogram--${line.key?.replace(':', '')}`}
            />
            <label
              className="block cursor-pointer w-full py-[4px] px-[8px]  capitalize text-gray-900 relative after:block after:absolute after:border-gray-600 after:mt-[-3px] after:right-[8px] after:top-1/2 after:-translate-y-1/2 after:border-b after:w-2 after:h-2 after:border-l after:-rotate-45 after:origin-center peer-checked:after:rotate-[135deg] peer-checked:after:mt-0 after:transition-all !body-4 !text-gray-600"
              htmlFor={`monogram--${line.key?.replace(':', '')}`}
            >
              Details
            </label>
            <div className="monogrammed max-h-0 overflow-hidden peer-checked:max-h-[500px] transition-all">
              <div className="pt-[8px] px-[8px] text-gray-900">
                <div className="mb-3 !body-4 !text-gray-600 [&_*]:font-inherit [&_*]:[font-size:inherit] [&>*]:!mt-0">
                  {monogramProperties()}
                </div>
                <p className="text-sale mb-3 !body-4 [&_*]:font-inherit [&_*]:[font-size:inherit]">
                  {monogramFinalSaleMessage}
                </p>
                <p className="text-text-default mb-3 !body-4 !text-gray-600 [&_*]:font-inherit [&_*]:[font-size:inherit]">
                  {monogramShippingWarning}
                </p>
                <button
                  className="font-GTAmerica underline underline-offset-[2.5px] !body-4 !text-gray-600 !decoration-gray-600"
                  onClick={removeMonogram}
                >
                  {window.TMW.Strings.cart.removeMonogram}
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    quantityInput: (
      <div className="flex border border-border-gray font-r-18 laptop:px-2 mr-5">
        <button
          className={'w-[10px] ml-2.5 laptop:w-9'}
          onClick={() => adjustQuantity(line.quantity - 1)}
          aria-label={window.TMW.Strings.cart.remove}
        >
          &#8722;
        </button>
        <input
          type="text"
          value={line.quantity}
          onBlur={(e) => adjustQuantity(parseInt(e.target.value, 10))}
          className="text-center w-[30px] py-1.5 px-0 border-0 heading-6 text-[12px] laptop:px-1"
        />
        <button
          className="w-[10px] mr-2.5 laptop:w-9"
          onClick={() => adjustQuantity(line.quantity + 1)}
          aria-label={window.TMW.Strings.cart.add}
        >
          &#43;
        </button>
      </div>
    ),
  };

  return (
    line && (
      <WishlistContext>
        <div
          className={
            isMiniCart
              ? 'flex gap-5 mb-10 last-of-type:mb-0'
              : 'flex gap-5 py-5 lg:mb-5 border-[#F4F1E6] border-b-[1px] border-solid md:last-of-type:border-none'
          }
          key={line.key}
          {...(DropShip ? { 'data-dropship-product': '' } : {})}
        >
          <div className="w-[120px] max-w-[120px] min-w-[120px] md:w-1/3">
            <a
              href={`${
                cart.mode === 'storefront'
                  ? `${Shopify.routes.root}${line.merchandise.product.handle}`
                  : line.url
              }`}
              className="no-underline body-3 md:text-sm text-text-default"
            >
              <Image
                {...line?.merchandise?.product?.featuredImage}
                src={
                  line?.featured_image?.url ||
                  line?.merchandise?.product?.featuredImage?.url
                }
              />
            </a>
          </div>
          <div className={`details ${!isMiniCart ? 'md:w-full' : 'w-full'}`}>
            <div className="flex justify-between gap-3">
              <span
                className={`h7--medium text-text-default ${
                  isMiniCart ? '' : 'md:text-sm'
                }`}
              >
                {line?.merchandise?.product?.vendor || line?.vendor}
              </span>
              <p className="flex gap-1 md:text-sm">
                {hasDiscount && (
                  <del className="inline-block body-3 text-text-disabled line-through">
                    {formatMoney(
                      line?.cost?.compareAtAmountPerQuantity ||
                        (line?.line_level_total_discount && {
                          amount: originalPrice,
                        }),
                    )}
                  </del>
                )}
                {hasCompareAtPrice && !hasDiscount && (
                  <del
                    className={
                      isMiniCart
                        ? 'inline-block body-3 text-text-disabled line-through'
                        : 'mr-2 product-price--strike-through body-3 !text-12 font-GTAmerica md:!text-14 leading-160'
                    }
                  >
                    {formatMoney({
                      amount: compareAtPrice * line.quantity,
                    })}
                  </del>
                )}
                <span
                  className={`body-3 font-GTAmerica !leading-160 ${
                    showRedFinalPrice ? 'text-sale' : 'text-text-subdued'
                  } ${isMiniCart ? '' : 'md:text-sm'}
                  `}
                >
                  {formatMoney({
                    amount:
                      line.vendor === 'DailyKarma'
                        ? line.final_line_price / 100
                        : (line.quantity * line.final_price) / 100,
                  })}
                </span>
              </p>
            </div>
            <a
              href={`${
                cart.mode === 'storefront'
                  ? `${Shopify.routes.root}${line.merchandise.product.handle}`
                  : line.url
              }`}
              className={`no-underline body-3 text-text-default ${
                isMiniCart
                  ? 'inline-block w-full max-w-[156px] leading-130'
                  : 'md:text-sm'
              }`}
            >
              {cart.mode === 'storefront'
                ? line.merchandise?.product.title
                : lineTitle}
            </a>
            {isMiniCart
              ? els.minicart.finalSaleBanner
              : els.default.finalSaleBanner}

            <div
              className={`${
                isFinalSale
                  ? isMiniCart
                    ? 'mt-2'
                    : 'mt-5 md:mt-6'
                  : isMiniCart
                  ? 'mt-2'
                  : 'mt-2.5 md:mt-5'
              }`}
            >
              {isMiniCart ? els.minicart.variantInfo : els.default.variantInfo}
            </div>

            {isMiniCart && hasDiscountAllocations && els.minicart.activeCoupons}
            {isMiniCart &&
              (giftProduct ||
                (line.properties && line.properties['gift message'])) && (
                <div className="body-3 flex justify-between items-center pt-1 mt-2 border border-b-0 border-r-0 border-l-0 border-gray-400">
                  <span>{window.TMW.Strings.cart.giftWrappingAdd}</span>
                  {giftProduct && (
                    <span>
                      {formatMoney({
                        amount: giftProduct.line_price / 100,
                      })}
                    </span>
                  )}
                </div>
              )}
            {!hideGift && (
              <button
                onClick={openGift}
                // eslint-disable-next-line max-len
                className={`flex bg-core-1 w-full justify-between items-center py-1.5 px-2 gap-12 ${
                  isMiniCart ? 'mt-2' : 'mt-2.5 lg:mt-5 md:w-[195px]'
                }`}
              >
                {isMiniCart ? (
                  <>
                    <div className="flex gap-2 items-center body-3 text-text-default">
                      <div className="[&>svg]:w-4">
                        {' '}
                        <IconCheckbox
                          checked={
                            giftProduct ||
                            (line.properties && line.properties['gift message'])
                          }
                        />
                      </div>
                      {window.TMW.Strings.cart.giftTitle}
                    </div>
                    <div className="-m-3 mr-0.5">
                      <IconGift />
                    </div>
                  </>
                ) : (
                  <>
                    <span className="flex gap-2 items-center body-3 text-text-default">
                      <IconGift />
                      {window.TMW.Strings.cart.giftTitle}
                    </span>
                    <IconCarat />
                  </>
                )}
              </button>
            )}
            {(giftProduct ||
              (line.properties && line.properties['gift message'])) && (
              <GiftLineItem
                isMiniCart={isMiniCart}
                product={giftProduct}
                message={line.properties['gift message']}
                configuredGiftProduct={configuredGiftProduct}
                userStatus={userStatus}
              />
            )}
            {canApplyDiscountCode && (
              <p className="final-sale-messaging mt-5 body-3 text-sale md:text-sm md:mt-6">
                {discountCodeMessaging}
              </p>
            )}
            {!isMiniCart && els.default.finalSaleDetails}
            {isMonogrammed &&
              (isMiniCart
                ? els.minicart.monogramInfo
                : els.default.monogramInfo)}
            <div
              className={`${
                isMiniCart ? 'mt-2' : 'mt-2.5 laptop:mt-0'
              } flex items-center
              laptop:basis-[39%] laptop:items-start
              laptop:mt-0 laptop:justify-center laptop:pl-[3%]`}
            >
              {isDonation ? '' : els.quantityInput}

              <button
                onClick={handleRemove}
                className="text-xs underline text-default"
              >
                {window.TMW.Strings.cart.remove}
              </button>
            </div>
            <button
              className={
                isMiniCart
                  ? 'body-3 mt-2 underline underline-offset-[2.5px]'
                  : 'text-xs md:text-sm mt-5 md:mt-6 underline underline-offset-[2.5px] text-default'
              }
              onClick={handleAddToWishlist}
            >
              {window.TMW.Strings.cart.moveToFavorites}
            </button>
          </div>
        </div>
      </WishlistContext>
    )
  );
};

const GiftLineItem = ({
  isMiniCart,
  product,
  message,
  configuredGiftProduct,
  userStatus,
}) => {
  const showVIPHeader =
    userStatus &&
    window.Shopify.country === 'US' &&
    product.discounts.find((disc) => disc.amount === product.price);

  return (
    <div
      className={`p-2.5 bg-core-1 ${
        !isMiniCart ? 'mt-2.5 md:w-[195px]' : 'pt-[4px]'
      }`}
    >
      {product && (
        <div
          className={`flex mb-2 gap-2 ${
            !isMiniCart ? '' : 'bg-white items-center'
          }`}
        >
          <Image
            classes={!isMiniCart ? 'w-[58px]' : 'w-10 h-10'}
            src={configuredGiftProduct?.featured_image}
            alt={product?.featured_image.alt}
          />
          <div>
            <h5 className="body-3 text-text-disabled">
              {showVIPHeader ? (
                <>
                  {window.TMW.Strings.cart.giftWrapProductHeadingVip}{' '}
                  {
                    <>
                      <s>
                        {formatMoney({
                          amount: configuredGiftProduct.price / 100,
                        })}
                      </s>
                      {'):'}
                    </>
                  }
                </>
              ) : (
                !isMiniCart && window.TMW.Strings.cart.giftWrapProductHeading
              )}
            </h5>
            <h4
              className={` text-text-default ${
                !isMiniCart ? 'body-3' : 'body-3-gt leading-130 pr-1'
              }`}
            >
              {product.variant_title}
            </h4>
          </div>
        </div>
      )}
      {message && (
        <div>
          <p className="body-3 text-text-disabled">
            {window.TMW.Strings.cart.message}
          </p>
          <p className="body-3">
            {message.length > 28 ? `${message.substring(0, 28)}...` : message}
          </p>
        </div>
      )}
    </div>
  );
};

export default withCartContext(CartPageItem);
